/**
 * apiKeysApi.ts — Wrapper für die drei API-Key-Cloud-Functions.
 *
 * Muster: src/api/stripeApi.ts (httpsCallable via firebase/functions).
 * Alle drei Functions laufen in europe-west3 — die `functions`-Instanz in
 * firebaseConfig.ts ist bereits mit dieser Region initialisiert.
 */

import { httpsCallable, HttpsCallableResult } from "firebase/functions";
import { functions } from "../firebaseConfig";
import logger from "../utils/logger";

// ---------------------------------------------------------------------------
// Response-Shapes (müssen exakt mit apiKeyFunctions.ts übereinstimmen)
// ---------------------------------------------------------------------------

/** Repräsentation eines API-Keys in der Liste (KEIN Klartext, kein Hash). */
export interface ApiKeyListItem {
  keyId: string;
  maskedPrefix: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revoked: boolean;
  /** Fail-closed resolved auf dem Backend (nie leer, nie unbekannte Werte). */
  scopes: string[];
}

/** Response von generateApiKey — Klartext nur einmalig hier vorhanden. */
export interface GenerateApiKeyResponse {
  keyId: string;
  plaintextKey: string;
  maskedPrefix: string;
  createdAt: string;
  /** Die tatsächlich resolvten (validierten) Scopes — kann von der Anfrage abweichen. */
  scopes: string[];
}

/** Response von listApiKeys. */
export interface ListApiKeysResponse {
  keys: ApiKeyListItem[];
}

/** Response von revokeApiKey. */
export interface RevokeApiKeyResponse {
  success: boolean;
}

/** Response von rotateApiKey — Klartext des NEUEN Keys nur einmalig hier vorhanden. */
export interface RotateApiKeyResponse {
  keyId: string;
  plaintextKey: string;
  maskedPrefix: string;
  createdAt: string;
  scopes: string[];
}

/**
 * Bekannte Scope-Werte für die Key-Generierung/-Rotation (SSOT für die FE-Auswahl).
 * MUSS mit `KNOWN_SCOPES` in `functions/src/apiKeys/apiKeyFunctions.ts` übereinstimmen.
 */
export const API_KEY_SCOPES = [
  "invoice:create",
  "invoice:read",
  "invoice:cancel",
  "webhook:manage",
] as const;

// ---------------------------------------------------------------------------
// Callable-Function-Referenzen
// ---------------------------------------------------------------------------

const generateApiKeyFn = httpsCallable<
  { label?: string; scopes?: string[] },
  GenerateApiKeyResponse
>(functions, "generateApiKey");

const listApiKeysFn = httpsCallable<Record<string, never>, ListApiKeysResponse>(
  functions,
  "listApiKeys"
);

const revokeApiKeyFn = httpsCallable<{ keyId: string }, RevokeApiKeyResponse>(
  functions,
  "revokeApiKey"
);

const rotateApiKeyFn = httpsCallable<{ keyId: string }, RotateApiKeyResponse>(
  functions,
  "rotateApiKey"
);

// ---------------------------------------------------------------------------
// Öffentliche API
// ---------------------------------------------------------------------------

/**
 * Generiert einen neuen API-Key für den eingeloggten User.
 * Der zurückgegebene `plaintextKey` ist der einzige Zeitpunkt, zu dem der
 * Klartext verfügbar ist — er wird NICHT persistiert und ist nach Page-Reload
 * unwiederbringlich weg.
 *
 * @param label Optionaler Anzeigename (z.B. „Solar-Shop Prod").
 * @param scopes Optionale Scope-Auswahl (nur durchgereicht, wenn gesetzt und nicht leer).
 *   Unbekannte/fehlende Werte werden serverseitig fail-closed auf DEFAULT_SCOPES normalisiert.
 * @returns GenerateApiKeyResponse inkl. einmaligem Klartext-Key.
 * @throws Wirft einen Error, wenn die Function fehlschlägt (z.B. apiAccess !== true).
 */
export async function generateApiKey(
  label?: string,
  scopes?: string[]
): Promise<GenerateApiKeyResponse> {
  logger.info("apiKeysApi: generateApiKey called");
  try {
    const result: HttpsCallableResult<GenerateApiKeyResponse> =
      await generateApiKeyFn({
        label: label?.trim() || undefined,
        scopes: scopes && scopes.length > 0 ? scopes : undefined,
      });
    logger.info("apiKeysApi: generateApiKey succeeded");
    return result.data;
  } catch (error: unknown) {
    logger.error("apiKeysApi: generateApiKey failed", error);
    throw error;
  }
}

/**
 * Listet alle API-Keys des eingeloggten Users.
 * Gibt ausschließlich maskierte Daten zurück — kein Klartext, kein Hash.
 *
 * @returns Array der vorhandenen Keys (inkl. widerrufener).
 * @throws Wirft einen Error bei Netzwerk- oder Auth-Fehlern.
 */
export async function listApiKeys(): Promise<ApiKeyListItem[]> {
  logger.info("apiKeysApi: listApiKeys called");
  try {
    const result: HttpsCallableResult<ListApiKeysResponse> =
      await listApiKeysFn({});
    logger.info(
      `apiKeysApi: listApiKeys returned ${result.data.keys.length} keys`
    );
    return result.data.keys;
  } catch (error: unknown) {
    logger.error("apiKeysApi: listApiKeys failed", error);
    throw error;
  }
}

/**
 * Widerruft einen API-Key des eingeloggten Users.
 * Setzt revoked=true; löscht den Record NICHT (Audit-Trail).
 *
 * @param keyId Die ID des zu widerrufenden Keys.
 * @returns `{ success: true }` bei Erfolg.
 * @throws Wirft einen Error, wenn der Key nicht gefunden wird oder kein Ownership besteht.
 */
export async function revokeApiKey(keyId: string): Promise<RevokeApiKeyResponse> {
  if (!keyId || keyId.trim() === "") {
    throw new Error("keyId darf nicht leer sein.");
  }
  logger.info(`apiKeysApi: revokeApiKey called for keyId ${keyId}`);
  try {
    const result: HttpsCallableResult<RevokeApiKeyResponse> =
      await revokeApiKeyFn({ keyId });
    logger.info(`apiKeysApi: revokeApiKey succeeded for keyId ${keyId}`);
    return result.data;
  } catch (error: unknown) {
    logger.error(`apiKeysApi: revokeApiKey failed for keyId ${keyId}`, error);
    throw error;
  }
}

/**
 * Rotiert einen bestehenden API-Key des eingeloggten Users: erzeugt einen neuen Key mit
 * denselben Scopes/Label, der alte Key bleibt für eine serverseitige Grace-Period gültig
 * (aktuell 24h, siehe `rotateApiKey`-Backend), bevor er automatisch gesperrt wird.
 *
 * @param keyId Die ID des zu rotierenden Keys.
 * @returns RotateApiKeyResponse inkl. einmaligem Klartext des NEUEN Keys.
 * @throws Wirft einen Error, wenn keyId leer ist, der Key nicht gefunden wird, kein
 *   Ownership besteht oder der Key bereits widerrufen ist.
 */
export async function rotateApiKey(keyId: string): Promise<RotateApiKeyResponse> {
  if (!keyId || keyId.trim() === "") {
    throw new Error("keyId darf nicht leer sein.");
  }
  logger.info(`apiKeysApi: rotateApiKey called for keyId ${keyId}`);
  try {
    const result: HttpsCallableResult<RotateApiKeyResponse> =
      await rotateApiKeyFn({ keyId });
    logger.info(`apiKeysApi: rotateApiKey succeeded for keyId ${keyId}`);
    return result.data;
  } catch (error: unknown) {
    logger.error(`apiKeysApi: rotateApiKey failed for keyId ${keyId}`, error);
    throw error;
  }
}
